import {cancelFrame, frame} from 'motion';

import {fragmentSource, shaderDefaults, vertexSource} from './halftoneClouds.util.js';

/** The reference cloud shader, rendered without React and bounded to 30fps. */
class HalftoneClouds extends HTMLElement {
    connectedCallback() {
        this.canvas = this.querySelector('canvas');
        this.settings = {...shaderDefaults};
        this.gl = this.canvas.getContext('webgl', {
            alpha: true, antialias: false, depth: false, stencil: false,
            premultipliedAlpha: false, powerPreference: 'low-power',
        });
        if (!this.gl) return;
        this.abort = new AbortController();
        this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
        this.elapsed = 0;
        this.previous = 0;
        this.visible = false;
        this.lost = false;
        this.tick = ({timestamp}) => {
            if (timestamp - this.previous < 1000 / 30) return;
            this.elapsed += Math.min((timestamp - this.previous) / 1000, 0.1) * this.settings.speed;
            this.previous = timestamp;
            this.draw();
        };
        const refresh = () => this.refresh();
        const options = {signal: this.abort.signal};
        this.reduced.addEventListener('change', refresh, options);
        document.addEventListener('visibilitychange', refresh, options);
        this.canvas.addEventListener('webglcontextlost', (event) => {
            event.preventDefault();
            this.lost = true;
            this.classList.remove('is-ready');
            cancelFrame(this.tick);
            this.program = null;
            this.buffer = null;
        }, options);
        this.canvas.addEventListener('webglcontextrestored', () => {
            this.lost = false;
            this.initialize();
        }, options);
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this.canvas);
        this.intersection = new IntersectionObserver(([entry]) => {
            this.visible = entry.isIntersecting;
            this.refresh();
        });
        this.intersection.observe(this);
        this.initialize();
    }

    compile(type, source) {
        const gl = this.gl;
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader;
        gl.deleteShader(shader);
        return null;
    }

    initialize() {
        const gl = this.gl;
        this.classList.remove('is-ready');
        const vertex = this.compile(gl.VERTEX_SHADER, vertexSource);
        const fragment = this.compile(gl.FRAGMENT_SHADER, fragmentSource);
        if (!vertex || !fragment) {
            if (vertex) gl.deleteShader(vertex);
            if (fragment) gl.deleteShader(fragment);
            return;
        }
        this.program = gl.createProgram();
        gl.attachShader(this.program, vertex);
        gl.attachShader(this.program, fragment);
        gl.linkProgram(this.program);
        gl.deleteShader(vertex);
        gl.deleteShader(fragment);
        if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
            gl.deleteProgram(this.program);
            this.program = null;
            return;
        }
        gl.useProgram(this.program);
        this.buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        const position = gl.getAttribLocation(this.program, 'a_position');
        gl.enableVertexAttribArray(position);
        gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
        this.uniforms = Object.fromEntries([
            'resolution', 'tint', 'time', 'scale', 'detail', 'warp', 'grain',
            'pixelSize', 'contrast', 'rotation', 'seed', 'clearCenter', 'colorStrength',
        ].map((name) => [name, gl.getUniformLocation(this.program, `u_${name}`)]));
        // Reference halftone settings, with a single paper-colored tint over brand red.
        gl.uniform1f(this.uniforms.colorStrength, 0);
        gl.uniform3f(this.uniforms.tint, 1, 0.98, 0.95);
        this.resize();
        this.classList.add('is-ready');
    }

    draw() {
        if (!this.program || this.lost) return;
        const gl = this.gl;
        gl.useProgram(this.program);
        gl.uniform2f(this.uniforms.resolution, this.canvas.width, this.canvas.height);
        gl.uniform1f(this.uniforms.time, this.elapsed);
        gl.uniform1f(this.uniforms.scale, 1 / this.settings.textureScale);
        gl.uniform1f(this.uniforms.rotation, this.settings.rotation * Math.PI / 180);
        for (const name of ['detail', 'warp', 'grain', 'contrast', 'seed', 'clearCenter']) {
            gl.uniform1f(this.uniforms[name], this.settings[name]);
        }
        gl.uniform1f(this.uniforms.pixelSize, this.settings.pixelSize * this.canvas.width / Math.max(1, this.canvas.clientWidth));
        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    updateSettings(settings) {
        Object.assign(this.settings, settings);
        if (this.gl) this.refresh();
    }

    resize() {
        if (this.lost) return;
        const ratio = Math.min(devicePixelRatio || 1, 1.5, 1920 / Math.max(1, this.canvas.clientWidth));
        this.canvas.width = Math.max(1, Math.round(this.canvas.clientWidth * ratio));
        this.canvas.height = Math.max(1, Math.round(this.canvas.clientHeight * ratio));
        this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        this.refresh();
    }

    refresh() {
        cancelFrame(this.tick);
        this.draw();
        if (this.program && !this.lost && this.visible && !document.hidden && !this.reduced.matches && this.settings.speed > 0) {
            this.previous = performance.now();
            frame.update(this.tick, true);
        }
    }

    disconnectedCallback() {
        this.classList.remove('is-ready');
        if (!this.gl) return;
        cancelFrame(this.tick);
        this.abort.abort();
        this.resizeObserver.disconnect();
        this.intersection.disconnect();
        if (this.buffer) this.gl.deleteBuffer(this.buffer);
        if (this.program) this.gl.deleteProgram(this.program);
    }
}

customElements.define('fk-halftone-clouds', HalftoneClouds);
